import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { rootRoute } from './__root';
import { emailAccountKeys } from '@/features/email-accounts/queries';
import { connectAccountSearchSchema } from '@/features/email-accounts/search-schema';
import { ConnectAccountPendingState } from '@/features/email-accounts/ConnectAccountPendingState';
import { getProviderPresets } from '@/lib/email-accounts';
import { ACCESS } from '@/features/access/access';
import { requireRoleAccess } from '@/features/access/role-route-guard';

/**
 * Dedicated full-screen "connect an email account" route (TRU-296 follow-up).
 * Replaces the two-pane `ConnectAccountDialog` modal: the master (provider
 * catalog) and detail (OAuth / SMTP / bulk CSV / bulk workspace) panes now
 * live at `/email-accounts/connect`, with the selection driven by
 * `?provider=` and the Microsoft admin-consent deep-link by `?bulkWorkspace=`.
 */
export const connectAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/email-accounts/connect',
  beforeLoad: requireRoleAccess(ACCESS.connectAccount),
  validateSearch: connectAccountSearchSchema,
  // Prime the provider-preset catalog (staleTime: Infinity — see
  // `useProviderPresetsQuery`) so the master pane skips its loading skeleton
  // on a fresh navigation. Loader errors are swallowed; the page's own query
  // renders the retry-able error state.
  loader: ({ context: { queryClient } }) =>
    queryClient
      .ensureQueryData({
        queryKey: emailAccountKeys.providerPresets(),
        queryFn: () => getProviderPresets(),
        staleTime: Infinity,
      })
      .catch(() => undefined),
  // `pendingMs: 0` / `pendingMinMs: 0` — no debounce, no artificial display
  // floor (see `routes/email-accounts.$id.tsx` for the precedent/rationale).
  pendingComponent: ConnectAccountPendingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/email-accounts/ConnectAccountPage'),
    'ConnectAccountPage',
  ),
});
